import type { Game } from '../hooks/useGames';

// Formatta un valore monetario da centesimi a euro con formato italiano
export const formatCurrency = (valueInCents: string | number): string => {
    const cents = typeof valueInCents === 'string' ? parseInt(valueInCents, 10) : valueInCents;
    if (isNaN(cents)) return '0,00';
    
    const euroValue = cents / 100;
    return euroValue.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

// Formatta il jackpot da centesimi a euro con formato italiano
export const formatJackpot = (valueInCents: number): string => {
    const euroValue = valueInCents / 100;

    if (euroValue >= 1000000) {
        return `€${(euroValue / 1000000).toFixed(1)}M`;
    } else if (euroValue >= 1000) {
        return `€${(euroValue / 1000).toFixed(0)}K`;
    }

    return `€${euroValue.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

// Formatta il jackpot completo per il badge
export const formatJackpotBadge = (valueInCents: number): string => {
    const euroValue = valueInCents / 100;
    return `JP ${euroValue.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })} €`;
};

export const getVolatilityLabel = (volatility: string): string => {
    switch (volatility) {
        case '1':
        case '2':
            return 'Bassa';
        case '3':
            return 'Media';
        case '4':
        case '5':
            return 'Alta';
        default:
            return 'N/A';
    }
};

export const getVolatilityColor = (volatility: string): string => {
    switch (volatility) {
        case '1':
        case '2':
            return 'text-green-500';
        case '3':
            return 'text-yellow-500';
        case '4':
        case '5':
            return 'text-red-500';
        default:
            return 'text-gray-500';
    }
};

export const getRTPColor = (rtp: string): string => {
    const rtpValue = parseFloat(rtp);
    if (rtpValue >= 96) return 'text-green-500';
    if (rtpValue >= 94) return 'text-yellow-500';
    return 'text-red-500';
};

export const formatRTP = (rtp: string): string => {
    const rtpValue = parseFloat(rtp);
    return `${rtpValue.toFixed(2)}%`;
};

// Formatta le funzionalità e volatilità per il display sotto al gioco
export const formatGameDetails = (game: Game): string => {
    const details = [];

    if (game.info?.functionality && game.info.functionality.length > 0) {
        details.push(game.info.functionality.join(', '));
    }

    if (game.info?.volatility) {
        details.push(`Vol. ${game.info.volatility}`);
    }

    return details.join(' | ');
};

export const getGameImageUrl = (game: Game, size: 'thumb' | 'landscape' = 'thumb'): string => {
    const imageUrl = size === 'landscape'
        ? game.images?.landscapeImgURL || game.images?.thumbURL
        : game.images?.thumbURL || game.images?.landscapeImgURL;

    return imageUrl || '/placeholder-game.jpg';
};

export const filterGamesByTheme = (games: Game[], theme: string): Game[] => {
    return games.filter(game =>
        game.info?.theme?.some(t =>
            t.toLowerCase().includes(theme.toLowerCase())
        )
    );
};

export const filterGamesByProvider = (games: Game[], provider: string): Game[] => {
    return games.filter(game =>
        game.provider.toLowerCase().includes(provider.toLowerCase())
    );
};

export const filterGamesByFeature = (games: Game[], feature: string): Game[] => {
    return games.filter(game =>
        game.info?.functionality?.some(f =>
            f.toLowerCase().includes(feature.toLowerCase())
        )
    );
};

export const sortGamesByRTP = (games: Game[], order: 'asc' | 'desc' = 'desc'): Game[] => {
    return [...games].sort((a, b) => {
        const rtpA = parseFloat(a.info?.rtp || '0');
        const rtpB = parseFloat(b.info?.rtp || '0');
        return order === 'desc' ? rtpB - rtpA : rtpA - rtpB;
    });
};

export const sortGamesByJackpot = (games: Game[], order: 'asc' | 'desc' = 'desc'): Game[] => {
    return [...games].sort((a, b) => {
        const jackpotA = a.info?.jackpotValue || 0;
        const jackpotB = b.info?.jackpotValue || 0;
        return order === 'desc' ? jackpotB - jackpotA : jackpotA - jackpotB;
    });
};

export const getGamesByPopularity = (games: Game[]): Game[] => {
    // Logica per ordinare per popolarità
    // Potresti basarti su: numero di giocatori, revenue, rating, ecc.
    // Per ora ordiniamo per RTP alto come proxy di qualità
    return sortGamesByRTP(games, 'desc');
};

export const searchGames = (games: Game[], query: string): Game[] => {
    const searchTerm = query.toLowerCase().trim();

    if (!searchTerm) return games;

    return games.filter(game => {
        const matchName = game.name.toLowerCase().includes(searchTerm);
        const matchProvider = game.provider.toLowerCase().includes(searchTerm);
        const matchThemes = game.info?.theme?.some(theme =>
            theme.toLowerCase().includes(searchTerm)
        );
        const matchFeatures = game.info?.functionality?.some(feature =>
            feature.toLowerCase().includes(searchTerm)
        );

        return matchName || matchProvider || matchThemes || matchFeatures;
    });
};

export const getGameStats = (games: Game[]) => {
    const totalGames = games.length;
    const jackpotGames = games.filter(game => game.info?.jackpot).length;
    const avgRTP = games.reduce((sum, game) => {
        const rtp = parseFloat(game.info?.rtp || '0');
        return sum + rtp;
    }, 0) / totalGames;

    const providers = [...new Set(games.map(game => game.provider))];
    const themes = [...new Set(games.flatMap(game => game.info?.theme || []))];

    return {
        totalGames,
        jackpotGames,
        avgRTP: avgRTP.toFixed(2),
        providersCount: providers.length,
        themesCount: themes.length,
        providers,
        themes
    };
};

export const generateGameUrl = (game: Game, mode: 'demo' | 'real' = 'demo'): string => {
    const baseUrl = import.meta.env.VITE_GAME_BASE_URL || 'https://game-server.snai.it';
    const params = new URLSearchParams({
        game: game.gameCode,
        mode,
        lang: 'it'
    });

    return `${baseUrl}/launch?${params.toString()}`;
};

export const isGameAvailable = (): boolean => {
    // Logica per verificare se il gioco è disponibile
    // Potresti controllare: manutenzione, restrizioni geografiche, ecc.
    return true; // Per ora ritorna sempre true
};

export const getGameRecommendations = (currentGame: Game, allGames: Game[], limit: number = 4): Game[] => {
    // Algoritmo semplice di raccomandazione basato su:
    // 1. Stesso provider
    // 2. Stessi temi
    // 3. RTP simile

    const recommendations = allGames
        .filter(game => game.id !== currentGame.id)
        .map(game => {
            let score = 0;

            // Stesso provider (+3 punti)
            if (game.provider === currentGame.provider) {
                score += 3;
            }

            // Temi in comune (+2 punti per tema)
            const commonThemes = game.info?.theme?.filter(theme =>
                currentGame.info?.theme?.includes(theme)
            ) || [];
            score += commonThemes.length * 2;

            // RTP simile (+1 punto se differenza < 2%)
            if (game.info?.rtp && currentGame.info?.rtp) {
                const rtpDiff = Math.abs(
                    parseFloat(game.info.rtp) - parseFloat(currentGame.info.rtp)
                );
                if (rtpDiff < 2) score += 1;
            }

            return { game, score };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map(item => item.game);

    return recommendations;
};